import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { DesignationService } from "@/services/Designation/DesignationService"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Pencil, Plus, Trash, TriangleAlert } from "lucide-react"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { designation } from "@/interface/designation.interface"
import CommonFilter from "@/components/common/CommonFilter"
// import { Pagination } from "@/components/ui/pagination"
import PaginationComponent from "@/components/common/PaginationComponent"

const Designation: React.FC = () => {
    const navigate = useNavigate()
    const [designations, setDesignations] = useState<designation[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const handleGetDesignations = async () => {
        try {
            const response = await DesignationService?.getDesignation();
            if (response?.status) {
                setDesignations(response?.data?.designation)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    const handleDeleteDesignation = async () => {
        try {
            const response = await DesignationService?.deleteDesignation(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                handleGetDesignations();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    useEffect(() => {
        handleGetDesignations();
    }, []);

    const toggleDeleteDialog = (id: string | undefined) => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const columns: TableColumn<designation>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: designation, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'name',
            header: 'Designation Name',
            render: (item: designation) => (
                <span className="font-semibold capitalize">{item?.name}</span>
            )
        },
        {
            key: 'level',
            header: 'Level',
            render: (item: designation) => (
                <span className="font-medium">{item?.level || 0}</span>
            )
        },
        // {
        //     key: 'isParent',
        //     header: 'Is Parent',
        //     render: (item: designation) => (
        //         <span>{item?.isParent === "true" ? "Yes" : "No"}</span>
        //     )
        // },
        {
            key: 'status',
            header: 'Status',
            render: (item: designation) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.isActive ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: designation) => (
                <div className="flex gap-2">
                    <Button
                        size={'icon'}
                        onClick={() => navigate(`${RoutesLink.editDesignation}/${item?.id}`)}
                        className="bg-primary/10 text-primary hover:bg-primary/20"
                        title="Edit Designation"
                    >
                        <Pencil size={18} />
                    </Button>
                    <Button
                        size={'icon'}
                        onClick={() => toggleDeleteDialog(item?.id)}
                        className="bg-destructive/10 text-destructive hover:bg-destructive/20"
                        title="Delete Designation"
                    >
                        <Trash size={18} />
                    </Button>
                </div>
            ),
        },
    ]
    const paginationData = {
        pageSize: 10,
        currentPage: 1,
        total: 100,
        totalPages: 10,
    };

    return (
        <>
            <Layout>
                <div className="space-y-6">
                    <MainHeading
                        title="Designations"
                        isBackButtonVisible={true}
                        buttonText='Add Designation'
                        buttonIcon={<Plus />}
                        onClick={() => { navigate(RoutesLink.addDesignation) }}
                    />
                    <CommonFilter />
                    <CommonTable data={designations} columns={columns} />
                    <PaginationComponent paginationData={paginationData} onPageChange={(page) => console.log(page)} />
                </div>
            </Layout>

            <CommonModal
                title=""
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>
                        <h2 className="mt-5 text-2xl font-bold text-gray-900">
                            Delete Designation?
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, all designation data will be
                            permanently removed from our servers.
                        </p>
                    </div>
                    <div className="mt-8 flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => toggleDeleteDialog("")}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeleteDesignation}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </>
    )
}

export default Designation;
